import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Subtitles, Upload, LogOut, Video, Trash2, Clock, 
  FileText, Crown, AlertCircle, CheckCircle2, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, isPro, checkAuth } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const fetchVideos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/videos`, { withCredentials: true });
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check payment status on return from Stripe
  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${API}/checkout/status/${sessionId}`,
          { withCredentials: true }
        );
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          await checkAuth(); // Refresh user data
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus('expired');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentStatus('pending');
        }
      } catch (error) {
        console.error('Payment status error:', error);
        setPaymentStatus('error');
      } finally {
        setCheckingPayment(false);
      }
    };
    
    poll();
  }, [checkAuth]);

  useEffect(() => {
    fetchVideos();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
    }
  }, [fetchVideos, searchParams, checkPaymentStatus]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      setError('Formato não suportado. Use MP4, MOV, AVI ou WebM.');
      return;
    }

    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      setError('Arquivo muito grande. Máximo 500MB.');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API}/videos/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      // Navigate to editor
      navigate(`/editor/${data.id}`);
    } catch (error) {
      setError(error.response?.data?.detail || 'Falha no upload do vídeo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este vídeo?')) return;
    
    try {
      await axios.delete(`${API}/videos/${videoId}`, { withCredentials: true });
      setVideos(videos.filter(v => v.id !== videoId));
    } catch (error) {
      setError('Falha ao excluir vídeo');
    }
  };

  const handleUpgrade = async () => {
    try {
      const { data } = await axios.post(
        `${API}/checkout/create`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      setError(error.response?.data?.detail || 'Falha ao iniciar pagamento');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-neutral-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Concluído';
      case 'processing': return 'Processando';
      case 'error': return 'Erro';
      default: return 'Aguardando';
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#FF3B30] rounded-sm flex items-center justify-center">
              <Subtitles className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Outfit'] text-xl font-bold text-white">Legenda AI</span>
          </div>

          <div className="flex items-center gap-4">
            <div className={`tier-badge ${isPro ? 'pro' : 'free'}`}>
              {isPro ? (
                <>
                  <Crown className="w-3 h-3 mr-1" />
                  Pro
                </>
              ) : 'Grátis'}
            </div>
            
            <div className="text-sm text-neutral-400">
              {user?.name}
            </div>

            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-neutral-400 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Payment Status Messages */}
        {paymentStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-sm flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <span className="text-green-400">
              Parabéns! Seu upgrade para Pro foi concluído com sucesso.
            </span>
          </motion.div>
        )}

        {paymentStatus === 'cancelled' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-sm flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <span className="text-yellow-400">
              O pagamento foi cancelado. Você pode tentar novamente quando quiser.
            </span>
          </motion.div>
        )}

        {checkingPayment && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-sm flex items-center gap-3"
          >
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <span className="text-blue-400">
              Verificando status do pagamento...
            </span>
          </motion.div>
        )}

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div
            data-testid="upload-zone"
            className={`upload-zone rounded-sm p-12 text-center cursor-pointer transition-all ${
              dragActive ? 'dragging' : ''
            } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !uploading && document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              data-testid="file-input"
              type="file"
              accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
              className="hidden"
              onChange={handleFileInput}
              disabled={uploading}
            />

            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-12 h-12 text-[#FF3B30] mx-auto animate-spin" />
                <div>
                  <p className="font-['Outfit'] text-xl font-bold text-white mb-2">
                    Fazendo upload...
                  </p>
                  <div className="w-64 mx-auto bg-[#1E1E1E] rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-[#FF3B30] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-neutral-400 text-sm mt-2">{uploadProgress}%</p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                <p className="font-['Outfit'] text-xl font-bold text-white mb-2">
                  Arraste seu vídeo ou clique para fazer upload
                </p>
                <p className="text-neutral-400 text-sm">
                  Formatos: MP4, MOV, AVI, WebM • Máximo 500MB
                  {!isPro && <span className="text-[#FF3B30]"> • Limite de 1 minuto (conta grátis)</span>}
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
        </motion.div>

        {/* Pro upgrade banner (for free users) */}
        {!isPro && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 p-6 bg-gradient-to-r from-[#FF3B30]/10 to-transparent border border-[#FF3B30]/20 rounded-sm flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF3B30]/20 rounded-sm flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#FF3B30]" />
              </div>
              <div>
                <h3 className="font-['Outfit'] font-bold text-white">Upgrade para Pro - $9.99</h3>
                <p className="text-neutral-400 text-sm">
                  Vídeos ilimitados, download com legendas embutidas e edição avançada
                </p>
              </div>
            </div>
            <Button 
              data-testid="upgrade-pro-btn"
              onClick={handleUpgrade}
              className="bg-[#FF3B30] hover:bg-[#D63026] text-white"
            >
              Fazer Upgrade
            </Button>
          </motion.div>
        )}

        {/* Videos List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-4">Seus Vídeos</h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#FF3B30] animate-spin" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12 bg-[#141414] border border-white/10 rounded-sm">
              <Video className="w-12 h-12 text-neutral-500 mx-auto mb-4" />
              <p className="text-neutral-400">Nenhum vídeo ainda</p>
              <p className="text-neutral-500 text-sm">Faça upload do seu primeiro vídeo para começar</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {videos.map((video) => (
                <div 
                  key={video.id}
                  className="bg-[#141414] border border-white/10 rounded-sm p-4 flex items-center justify-between hover:bg-[#1E1E1E] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1E1E1E] rounded-sm flex items-center justify-center">
                      <Video className="w-6 h-6 text-neutral-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{video.original_filename}</h3>
                      <div className="flex items-center gap-3 text-sm text-neutral-400">
                        <span className="flex items-center gap-1">
                          {getStatusIcon(video.status)}
                          {getStatusText(video.status)}
                        </span>
                        {video.language_detected && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {video.language_detected.toUpperCase()}
                          </span>
                        )}
                        <span>
                          {new Date(video.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`edit-video-${video.id}`}
                      onClick={() => navigate(`/editor/${video.id}`)}
                      className="text-neutral-400 hover:text-white hover:bg-white/5"
                    >
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-video-${video.id}`}
                      onClick={() => handleDelete(video.id)}
                      className="text-neutral-400 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default Dashboard;
